import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    FileTypeBreadcrumbs,
    FileTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FormTemplateFileTypeActions from "../../../actions/formTemplateFileTypes";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./FormTemplateFileTypePage.scss";

interface FormTemplateFileTypePageProps {
    form: Dtos.FormTemplateFileType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formTemplateFileType: Dtos.FormTemplateFileType;
    lookups: Dtos.Lookup[];
    loadingFormTemplateFileType: boolean;
    loadFormTemplateFileTypeFailure: boolean;
    loadFormTemplateFileTypeErrorCode: string;
    savingFormTemplateFileType: boolean;
    saveFormTemplateFileTypeSuccess: boolean;
    saveFormTemplateFileTypeFailure: boolean;
    creatingFormTemplateFileType: boolean;
    createFormTemplateFileTypeSuccess: boolean;
    validatingFormTemplateFileType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearFormTemplateFileType: FormTemplateFileTypeActions.IFormTemplateFileTypeClearActionCreator;
    loadFormTemplateFileType: FormTemplateFileTypeActions.IFormTemplateFileTypeLoadByIdActionCreator;
    saveFormTemplateFileType: FormTemplateFileTypeActions.IFormTemplateFileTypeSaveActionCreator;
    createFormTemplateFileType: FormTemplateFileTypeActions.IFormTemplateFileTypeCreateActionCreator;
    validateFormTemplateFileType: FormTemplateFileTypeActions.IFormTemplateFileTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.formTemplateFileType";

const FormTemplateFileTypePage: FC<FormTemplateFileTypePageProps> = ({
    form,
    formState,
    formProperties,
    formTemplateFileType,
    lookups,
    loadingFormTemplateFileType,
    loadFormTemplateFileTypeFailure,
    loadFormTemplateFileTypeErrorCode,
    savingFormTemplateFileType,
    saveFormTemplateFileTypeSuccess,
    saveFormTemplateFileTypeFailure,
    creatingFormTemplateFileType,
    createFormTemplateFileTypeSuccess,
    validatingFormTemplateFileType,
    validationFailures,
    permissions,
    clearFormTemplateFileType,
    loadFormTemplateFileType,
    saveFormTemplateFileType,
    createFormTemplateFileType,
    validateFormTemplateFileType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { formTemplateFileTypeId } = useParams<{
        formTemplateFileTypeId: string;
    }>();
    const formTemplateFileTypeIdNumber = parseInt(formTemplateFileTypeId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.FormTemplateFileType =
            {} as Dtos.FormTemplateFileType;

        loadForm(
            reduxFormName,
            Object.assign({ ...formTemplateFileType }, extraData)
        );
    };


    // Load file type
    useEffect(() => {
        if (
            formTemplateFileTypeIdNumber &&
            !formTemplateFileType &&
            !loadingFormTemplateFileType
        ) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(FileTypeBreadcrumbs(formTemplateFileTypeIdNumber));
            loadFormTemplateFileType(formTemplateFileTypeIdNumber);
        } else if (
            !formTemplateFileTypeIdNumber &&
            !creatingFormTemplateFileType &&
            !createFormTemplateFileTypeSuccess
        ) {
            createFormTemplateFileType();
            setBreadcrumbs(FileTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (formTemplateFileType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        formTemplateFileTypeIdNumber,
        formTemplateFileType,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        FileTypeBreadcrumbs,
        loadFormTemplateFileType,
        createFormTemplateFileType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingFormTemplateFileType,
        creatingFormTemplateFileType,
        createFormTemplateFileTypeSuccess,
        FileTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFormTemplateFileTypeFailure) {
            navigate(`/error/${loadFormTemplateFileTypeErrorCode}`);
        }
    }, [
        loadFormTemplateFileTypeFailure,
        loadFormTemplateFileTypeErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (formTemplateFileTypeIdNumber && formTemplateFileType?.value) {
            setBreadcrumbs(
                FileTypeBreadcrumbs(
                    formTemplateFileTypeIdNumber,
                    formTemplateFileType?.value
                )
            );
        }
    }, [
        formTemplateFileTypeIdNumber,
        formTemplateFileType,
        setBreadcrumbs,
        FileTypeBreadcrumbs,
    ]);

    // Reload file type after saving
    useEffect(() => {
        if (!savingFormTemplateFileType && saveFormTemplateFileTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>File type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/formtemplatefiletypes";
                navigate(url);
            } else if (!formTemplateFileTypeIdNumber) {
                let url: string = `/settings/qa/formtemplatefiletypes/${formTemplateFileType?.id}`;
                navigate(url);
                clearFormTemplateFileType();
            }
        }
    }, [
        savingFormTemplateFileType,
        saveFormTemplateFileTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingFormTemplateFileType && saveFormTemplateFileTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingFormTemplateFileType, saveFormTemplateFileTypeFailure]);

    // Cleanup effect to clear the file type on component unmount
    useEffect(() => {
        return () => {
            clearFormTemplateFileType();
            resetForm(reduxFormName);
        };
    }, [clearFormTemplateFileType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/formtemplatefiletypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.FormTemplateFileType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.FormTemplateFileType =
                {} as Dtos.FormTemplateFileType;

            await saveFormTemplateFileType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.FormTemplateFileType =
                {} as Dtos.FormTemplateFileType;

            if (form && hasBeenSubmitted && !validatingFormTemplateFileType) {
                await validateFormTemplateFileType(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.FormTemplateFileTypesUpdate,
                Dtos.Permission.FormTemplateFileTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingFormTemplateFileType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "formTemplateFileType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "formTemplateFileType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "formTemplateFileType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "formTemplateFileType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingFormTemplateFileType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingFormTemplateFileType}
            permission={Dtos.Permission.FormTemplateFileTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<FormTemplateFileTypePageProps> => {
    let formTemplateFileType: Dtos.FormTemplateFileType | undefined = !(
        state.formTemplateFileTypes.formData instanceof Array
    )
        ? state.formTemplateFileTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formTemplateFileType: formTemplateFileType!,
        loadingFormTemplateFileType:
            state.formTemplateFileTypes.loadState &&
            state.formTemplateFileTypes.loadState.status ==
                RequestState.Pending,
        loadFormTemplateFileTypeFailure:
            state.formTemplateFileTypes.loadState &&
            state.formTemplateFileTypes.loadState.status ==
                RequestState.Failure,
        loadFormTemplateFileTypeErrorCode:
            state.formTemplateFileTypes.loadState.errorCode,
        savingFormTemplateFileType:
            state.formTemplateFileTypes.saveState &&
            state.formTemplateFileTypes.saveState.status ==
                RequestState.Pending,
        saveFormTemplateFileTypeSuccess:
            state.formTemplateFileTypes.saveState &&
            state.formTemplateFileTypes.saveState.status ==
                RequestState.Success,
        saveFormTemplateFileTypeFailure:
            state.formTemplateFileTypes.saveState &&
            state.formTemplateFileTypes.saveState.status ==
                RequestState.Failure,
        creatingFormTemplateFileType:
            state.formTemplateFileTypes.createState &&
            state.formTemplateFileTypes.createState.status ==
                RequestState.Pending,
        createFormTemplateFileTypeSuccess:
            state.formTemplateFileTypes.createState &&
            state.formTemplateFileTypes.createState.status ==
                RequestState.Success,
        validatingFormTemplateFileType:
            state.formTemplateFileTypes.validateState &&
            state.formTemplateFileTypes.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.formTemplateFileType,
        formState: state.formTemplateFileTypes.formState,
        formProperties: state.formTemplateFileTypes.formProperties,
        validationFailures: formTemplateFileType
            ? state.formTemplateFileTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<FormTemplateFileTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadFormTemplateFileType: bindActionCreators(
            FormTemplateFileTypeActions.LoadFormTemplateFileTypeById,
            dispatch
        ),
        clearFormTemplateFileType: bindActionCreators(
            FormTemplateFileTypeActions.Clear,
            dispatch
        ),
        saveFormTemplateFileType: bindActionCreators(
            FormTemplateFileTypeActions.SaveFormTemplateFileType,
            dispatch
        ),
        createFormTemplateFileType: bindActionCreators(
            FormTemplateFileTypeActions.CreateFormTemplateFileType,
            dispatch
        ),
        validateFormTemplateFileType: bindActionCreators(
            FormTemplateFileTypeActions.ValidateFormTemplateFileType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FormTemplateFileTypePage);
